import { roundTo } from './util-functions'

/**
 * The base rate after the IRO's rounding mode is applied.
 *
 * Falls back to the unrounded rate when no mode is set or when `roundTo`
 * cannot produce a number, so a blank rounding never turns the rate into NaN.
 */
export function roundBaseRate(
  baseRate: unknown,
  rounding: string | null | undefined,
): number {
  const base = Number(baseRate) || 0
  if (!rounding) return base
  const rounded = Number(roundTo(base, rounding))
  return Number.isFinite(rounded) ? rounded : base
}

export interface RateComponents {
  interestBaseRate?: unknown
  rounding?: string | null
  margin?: unknown
  casValue?: unknown
  pikPremium?: unknown
}

/**
 * The all-in rate as the forms display it: ROUNDED base rate + margin + CAS +
 * PIK premium. Every form that previews an all-in rate derives it here so the
 * rounded base rate and the all-in rate can never disagree; the ledger derives
 * the same figures on save and its values win on reload.
 */
export function deriveAllInRate(components: RateComponents): {
  roundedBaseRate: number
  allInRate: number
} {
  const roundedBaseRate = roundBaseRate(
    components.interestBaseRate,
    components.rounding,
  )
  const allInRate =
    roundedBaseRate +
    (Number(components.margin) || 0) +
    (Number(components.casValue) || 0) +
    (Number(components.pikPremium) || 0)
  return { roundedBaseRate, allInRate }
}

/**
 * True when a form field does not already hold the derived rate. Lets an
 * effect that derives a rate list that field among its dependencies without
 * looping: writing the derived value makes the two equal, so it fires once.
 * Non-numeric current values (blank, NaN, undefined) always count as differing.
 */
export function rateDiffers(current: unknown, derived: number): boolean {
  const cur = Number(current)
  return !Number.isFinite(cur) || Math.abs(cur - derived) > 1e-9
}

/**
 * Display a rate input's value as a percentage with five decimals. The IRO's
 * rounding mode applies to the BASE rate only; the all-in rate (which carries
 * the margin, CAS and PIK premium) and the CAS itself must be shown as they
 * are, or a 0.05 premium under nearest-quarter rounding disappears from view.
 */
export function formatRatePercent(value: string | number | undefined | null): string {
  const n = parseFloat(String(value ?? ''))
  return Number.isNaN(n) ? '%' : `${n.toFixed(5)} %`
}
